import * as SentryReact from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import App from './App';
import {
  IGNORED_AXIOS_ERROR_CODES,
  type IgnoredAxiosErrorCode,
} from './modules/api/errorCodes';
import { colorScheme } from './modules/colorScheme';
import { deviceModule } from './modules/device';
import { webCampaign } from './modules/webCampaign';

webCampaign.init();
colorScheme.init();
deviceModule.init();

SentryReact.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  integrations: [
    SentryReact.browserTracingIntegration(),
    SentryReact.captureConsoleIntegration({
      levels: ['error'],
    }),
  ],
  release: 'w2w',
  enabled: process.env.NODE_ENV === 'production',
  tracesSampleRate: 0.01,
  beforeSend(event, hint) {
    const error = hint.originalException;

    if (error && isAxiosError(error)) {
      if (
        error.code &&
        IGNORED_AXIOS_ERROR_CODES.includes(error.code as IgnoredAxiosErrorCode)
      ) {
        return null;
      }
    }

    return event;
  },
});

const container = document.getElementById('root');
const root = createRoot(container!);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

root.render(
  <QueryClientProvider client={queryClient}>
    <StrictMode>
      <App />
    </StrictMode>
  </QueryClientProvider>,
);
